import type { SupportedLocale } from '../types';
import type { RecommendationCatalogBundle } from '../features/recommendations/catalog';

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || '/api';

interface RemoteRecommendationCatalogBundle extends Omit<RecommendationCatalogBundle, 'id'> {
  entryId?: string;
  id?: string;
}

interface RecommendationCatalogResponse {
  entries: RemoteRecommendationCatalogBundle[];
  locale: string;
  count: number;
}

const cache = new Map<string, RecommendationCatalogBundle[]>();

const makeUrl = (endpoint: string): string => {
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  return `${baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
};

const normalizeCatalogEntries = (entries: RemoteRecommendationCatalogBundle[]): RecommendationCatalogBundle[] =>
  entries
    .map((entry) => ({
      ...entry,
      id: entry.id || entry.entryId || ''
    }))
    .filter((entry): entry is RecommendationCatalogBundle => Boolean(entry.id));

export const recommendationCatalogService = {
  async getCatalog(locale: SupportedLocale): Promise<RecommendationCatalogBundle[]> {
    const cached = cache.get(locale);
    if (cached) {
      return cached;
    }

    const response = await fetch(makeUrl(`/recommendations/catalog?locale=${encodeURIComponent(locale)}`), {
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': locale
      }
    });

    if (!response.ok) {
      throw new Error(`Recommendation catalog request failed: ${response.status}`);
    }

    const payload = await response.json() as { success: boolean; data?: RecommendationCatalogResponse; error?: string };
    if (!payload.success || !payload.data) {
      throw new Error(payload.error || 'Recommendation catalog request failed');
    }

    const normalizedEntries = normalizeCatalogEntries(payload.data.entries);
    cache.set(locale, normalizedEntries);
    return normalizedEntries;
  },

  clearCache(locale?: SupportedLocale) {
    if (locale) {
      cache.delete(locale);
      return;
    }

    cache.clear();
  }
};
